'use client'
import React, { useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import './phone-input.css'
import { Button } from '@/components/Button'

function TextInput({
  label,
  required,
  ...props
}: React.ComponentPropsWithoutRef<'input'> & { label: string }) {
  const id = useId()

  return (
    <div className="group relative z-0 transition-all focus-within:z-10">
      <input
        type="text"
        id={id}
        {...props}
        required={required}
        placeholder=" "
        className="peer block w-full border border-neutral-300 bg-transparent px-6 pb-4 pt-12 text-base/6 text-neutral-950 ring-4 ring-transparent transition focus:border-neutral-950 focus:outline-none focus:ring-neutral-950/5 group-first:rounded-t-2xl group-last:rounded-b-2xl"
      />
      <label
        htmlFor={id}
        className="pointer-events-none absolute left-6 top-1/2 -mt-3 origin-left text-base/6 text-neutral-500 transition-all duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-neutral-950 peer-[:not(:placeholder-shown)]:-translate-y-4 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-neutral-950"
      >
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
    </div>
  )
}

function RadioInput({
  label,
  ...props
}: React.ComponentPropsWithoutRef<'input'> & { label: string }) {
  return (
    <label className="flex gap-x-3">
      <input
        type="radio"
        {...props}
        className="h-6 w-6 flex-none appearance-none rounded-full border border-neutral-950/20 outline-none checked:border-[0.5rem] checked:border-neutral-950 focus-visible:ring-1 focus-visible:ring-neutral-950 focus-visible:ring-offset-2"
      />
      <span className="text-base/6 text-neutral-950">{label}</span>
    </label>
  )
}

const ContactForm = () => {
  const form = useRef<HTMLFormElement>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [phone, setPhone] = useState<string | undefined>()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    const formData = new FormData(e.currentTarget)
    const data = {
      user_name: formData.get('user_name') as string,
      user_email: formData.get('user_email') as string,
      company: formData.get('company') as string,
      phone: phone || '',
      message: formData.get('message') as string,
      budget: formData.get('budget') as string,
      website: formData.get('website') as string, // honeypot
    }

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (result.success) {
        toast.success('Message sent successfully!', {
          description: 'We will get back to you soon.',
        })
        form.current?.reset()
        setPhone(undefined)
      } else if (result.issues) {
        const firstIssue = result.issues[0]
        toast.error('Validation error', {
          description: firstIssue.message,
        })
      } else {
        toast.error('Failed to send message', {
          description: result.error || 'Please try again later.',
        })
      }
    } catch {
      toast.error('Network error', {
        description: 'Please check your connection and try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form ref={form} onSubmit={handleSubmit}>
      <h2 className="font-display text-base font-semibold text-neutral-950">
        Work inquiries
      </h2>
      <div className="isolate mt-6 -space-y-px rounded-2xl bg-white/50">
        <TextInput
          label="Name"
          name="user_name"
          autoComplete="name"
          required
        />
        <TextInput
          label="Email"
          type="email"
          name="user_email"
          autoComplete="email"
          required
        />
        <TextInput
          label="Company"
          name="company"
          autoComplete="organization"
        />

        {/* Phone with country selector */}
        <div className="group relative z-0 border border-neutral-300 px-6 pb-4 pt-8 transition-all focus-within:z-10 focus-within:border-neutral-950">
          <label className="block text-sm font-medium text-neutral-500 mb-2">
            Phone
          </label>
          <PhoneInput
            international
            defaultCountry="FR"
            value={phone}
            onChange={setPhone}
            className="phone-input-custom"
          />
        </div>

        <TextInput label="Message" name="message" required />
        <div className="border border-neutral-300 px-6 py-8 first:rounded-t-2xl last:rounded-b-2xl">
          <fieldset>
            <legend className="text-base/6 text-neutral-500">Budget</legend>
            <div className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2">
              <RadioInput label="Undefined – $5K" name="budget" value="25" />
              <RadioInput label="$5K – $10K" name="budget" value="50" />
              <RadioInput label="$10K – $15K" name="budget" value="100" />
              <RadioInput label="More than $15K" name="budget" value="150" />
            </div>
          </fieldset>
        </div>
      </div>

      {/* Honeypot field - hidden from users, bots will fill this */}
      <input
        type="text"
        name="website"
        defaultValue=""
        className="absolute -left-[9999px] opacity-0"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <Button type="submit" className="mt-10" disabled={isSubmitting}>
        {isSubmitting ? 'Sending...' : 'Contact Us'}
      </Button>
    </form>
  )
}

export default ContactForm
